import {
    PropRuntimeGraph,
    PropRuntimeGraphBody,
    PropRuntimeNode,
} from '../../../shared/types/PropRuntimeGraph';
import { normalizeMagicDelta } from './propMechanics/PropMagicUtils';
import { PropEventBus } from './propMechanics/PropEventBus';
import {
    BattleRuntimeState,
    defaultPropRandomSource,
    PropActionResult,
    PropMechanicsDispatchResult,
    PropNodeValue,
    PropRandomSource,
    PropRuntimeEffect,
    PropRuntimeEvent,
    PropRuntimeEventReason,
    PropRuntimeReleaseCallback,
    PropRuntimeMutation,
    RuntimePermanentBoostRule,
    RuntimePropInstance,
    isPropActionNode,
} from './propMechanics/PropMechanicsTypes';
import { createRuntimeNode } from './propMechanics/runtimeNodes/RuntimeNodeFactory';
import {
    getDefaultOutputPort,
    isRuntimeActionNode,
} from './propMechanics/runtimeNodes/RuntimeNodeBase';
import {
    PropInstanceNodeRuntime,
    RuntimeAcquirePropOptions,
    RuntimeNodeContext,
    RuntimeNodeLike,
} from './propMechanics/runtimeNodes/RuntimeNodeTypes';
import {
    isEmptyPropInstance,
    toBoolean,
    toNumber,
    uniqueLiveItems,
} from './propMechanics/runtimeNodes/RuntimeNodeUtils';

interface RuntimeActionTask {
    context: PropNodeContext;
    nodeId: number;
    nodeType: string;
    currentTarget?: RuntimePropInstance;
    event: PropRuntimeEvent;
    result: PropMechanicsDispatchResult;
    priority: number;
    sourceTypeOrder: number;
    sourceOrder: number;
    flowOrder: number;
    flowPath: number[];
    sequence: number;
}

const ACTION_PRIORITY: Record<string, number> = {
    actionPurify: 100,
    actionProtect: 110,
    actionPermanentBoost: 200,
    actionRarityWeight: 200,
    actionExtendCommission: 200,
    actionSwapPosition: 300,
    actionAccelerate: 320,
    actionGrow: 400,
    actionExtraYield: 420,
    actionCollect: 430,
    actionHarvest: 440,
    actionSpawn: 500,
    actionTransform: 520,
    actionEliminate: 600,
    actionTriggerVisual: 700,
};

const MAX_ACTION_TASKS_PER_FLUSH = 5000;

/**
 * 道具机制引擎。
 *
 * 引擎负责解释 `PropRuntimeGraph` 并执行节点逻辑。它不依赖 UI、动画、资源节点或表现层对象，
 * 只处理纯运行时数据。节点实现采用 class 模型：每个节点类型对应一个 class，每个数据输出端口
 * 对应该 class 上的一个方法；输入端口通过调用连接节点的输出端口方法获得数据。
 */
export class PropsMechanicsEngine {
    private readonly eventBus: PropEventBus;
    private readonly rng: PropRandomSource;
    private instanceSeed = 0;
    private runtimeSeed = 0;
    private actionTaskSeed = 0;
    private actionBatchDepth = 0;
    private eventBatchDepth = 0;
    private flushingActionQueue = false;
    private flushingEventBatch = false;
    private readonly activeDispatchResults: PropMechanicsDispatchResult[] = [];
    private readonly emptyBoardPool: RuntimePropInstance[] = [];
    private readonly actionQueue: RuntimeActionTask[] = [];
    private readonly eventBatchQueue: PropRuntimeEvent[] = [];

    /**
     * 创建道具机制引擎。
     *
     * @param state 战斗运行时状态。
     * @param eventBus 可选事件总线；未传入时引擎会创建独立总线。
     * @param rng 可选随机源；未传入时使用默认随机源。
     */
    public constructor(
        private readonly state: BattleRuntimeState,
        eventBus?: PropEventBus,
        rng: PropRandomSource = defaultPropRandomSource,
    ) {
        this.eventBus = eventBus ?? new PropEventBus();
        this.rng = rng;
        this.eventBus.onAfterEmit(() => this.flushActionQueue());
    }

    /**
     * 开始一个动作调度批次。
     *
     * 批次期间，事件节点只会把动作节点加入全局队列，不会立刻执行。调用方应在一组语义上属于同一阶段的
     * 事件派发前调用该方法，例如一轮开始时依次触发配方、辅料和盘面原料。这样净化、保护等高优先级动作
     * 可以在整个阶段内先于消除等低优先级动作执行，避免由道具监听顺序造成结果不稳定。
     */
    public beginActionBatch(): void {
        this.actionBatchDepth += 1;
    }

    /**
     * 结束一个动作调度批次并尝试执行队列。
     *
     * 只有最外层批次结束时才会真正 flush。嵌套批次用于兼容动作执行过程中派发的新事件，避免队列在半途
     * 被提前结算。
     */
    public endActionBatch(): void {
        this.actionBatchDepth = Math.max(0, this.actionBatchDepth - 1);
        this.flushActionQueue();
    }

    /**
     * 开始一个运行时事件批次。
     *
     * 批次期间事件会先进入内部队列，不会立即派发。结束批次时会优先派发全部 propEnteredBoard，
     * 再派发倒计时刷新、位置动画等由入场引发的派生事件，保证批量入场阶段不会被其他事件穿插。
     */
    public beginEventBatch(): void {
        this.eventBatchDepth += 1;
        this.beginActionBatch();
    }

    /**
     * 结束一个运行时事件批次，并按入场优先规则统一释放事件。
     *
     * 事件批次会同步开启动作批次，因此批量释放 propEnteredBoard 时不会立刻执行监听器排入的动作；
     * 所有事件释放完成后再统一结算动作队列。
     */
    public endEventBatch(): void {
        this.eventBatchDepth = Math.max(0, this.eventBatchDepth - 1);
        if (this.eventBatchDepth <= 0) this.flushEventBatch();
        this.endActionBatch();
    }

    /**
     * 将一个动作节点加入全局调度队列。
     *
     * 调度队列按动作优先级、来源类型、来源顺序和入队顺序排序执行。调用该方法不会立刻执行动作；
     * 实际执行发生在当前事件派发完成后，或显式批次结束后。
     *
     * @param context 产生该动作的节点上下文。
     * @param nodeId 需要执行的动作节点 id。
     * @param currentTarget each 分支传递下来的当前目标。
     * @param event 当前动作所属的原始事件。
     * @param result 当前动作应写入的派发结果。
     * @param flowOrder 同一个 flow 端口中的连接顺序。
     */
    public enqueueAction(
        context: PropNodeContext,
        nodeId: number,
        currentTarget: RuntimePropInstance | undefined,
        event: PropRuntimeEvent,
        result: PropMechanicsDispatchResult,
        flowOrder: number,
        flowPath: number[] = [flowOrder],
    ): void {
        const node = context.getNode(nodeId);
        const owner = context.owner;
        this.actionTaskSeed += 1;
        const task: RuntimeActionTask = {
            context,
            nodeId,
            nodeType: node?.type ?? '',
            currentTarget,
            event,
            result,
            priority: ACTION_PRIORITY[node?.type ?? ''] ?? 400,
            sourceTypeOrder: this.getActionSourceTypeOrder(owner),
            sourceOrder: this.getActionSourceOrder(owner),
            flowOrder,
            flowPath,
            sequence: this.actionTaskSeed,
        };
        this.actionQueue.push(task);
    }

    /**
     * 获取事件总线。
     *
     * @returns 当前引擎使用的事件总线；表现层可订阅它播放动画或记录日志。
     */
    public getEventBus(): PropEventBus {
        return this.eventBus;
    }

    public emit(event: PropRuntimeEvent): void {
        if (this.eventBatchDepth > 0 || this.flushingEventBatch) {
            this.eventBatchQueue.push(event);
            return;
        }
        this.emitImmediate(event);
    }

    private emitImmediate(event: PropRuntimeEvent): void {
        this.eventBus.emit(event);
    }

    private flushEventBatch(): void {
        if (this.flushingEventBatch) return;
        this.flushingEventBatch = true;
        try {
            while (this.eventBatchQueue.length) {
                const timerEvents: PropRuntimeEvent[] = [];
                const deferredEvents: PropRuntimeEvent[] = [];
                while (this.eventBatchQueue.length) {
                    const currentEvents = this.eventBatchQueue.splice(0);
                    const enteredEvents: PropRuntimeEvent[] = [];
                    for (const event of currentEvents) {
                        if (event.type === 'propEnteredBoard') enteredEvents.push(event);
                        else if (event.type === 'propTimerChanged') timerEvents.push(event);
                        else deferredEvents.push(event);
                    }
                    for (const event of enteredEvents) this.emitImmediate(event);
                }

                while (timerEvents.length) this.emitImmediate(timerEvents.shift()!);

                while (this.eventBatchQueue.length) {
                    const currentEvents = this.eventBatchQueue.splice(0);
                    for (const event of currentEvents) {
                        if (event.type === 'propTimerChanged') timerEvents.push(event);
                        else deferredEvents.push(event);
                    }
                    while (timerEvents.length) this.emitImmediate(timerEvents.shift()!);
                }

                for (const event of deferredEvents) this.emitImmediate(event);
            }
        } finally {
            this.flushingEventBatch = false;
        }
    }

    /**
     * 将引擎挂载到事件总线上。
     *
     * 事件节点在道具实例化时会自行订阅事件；该方法只保留为生命周期挂载入口。
     */
    public attach(): void {
        // 事件节点在道具实例化时自行注册监听；这里保留空挂载点，方便外部保持原调用顺序。
    }

    /**
     * 注销引擎注册的事件监听。
     *
     * 通常在战斗销毁、场景切换或重新初始化时调用。
     */
    public detach(): void {
        for (const item of this.state.items) this.disposeNodeRuntime(item);
        this.eventBus.clearScope('props-mechanics-engine');
    }

    /**
     * 释放整局游戏持有的运行时资源。
     *
     * 该方法用于 `gameEnded`。它会清空盘面、注销所有节点监听、丢弃空位置对象池，
     * 并清空材料库、配方、辅料等运行列表，使同一个引擎实例不再保留旧局状态。
     */
    public releaseAll(): void {
        this.clearBoard(true);
        for (const item of this.state.items) this.disposeNodeRuntime(item);
        this.emptyBoardPool.length = 0;
        this.activeDispatchResults.length = 0;
        this.state.permanentBoostRules.length = 0;
        this.clearRoundYieldBoosts();
        this.state.items.length = 0;
        this.state.materialLibrary.length = 0;
        this.state.recipes.length = 0;
        this.state.consumables.length = 0;
        for (let row = 0; row < this.state.rows; row++) {
            if (!this.state.board[row]) this.state.board[row] = [];
            for (let col = 0; col < this.state.cols; col++) this.state.board[row][col] = '';
        }
        this.eventBus.clearScope('props-mechanics-engine');
    }

    /**
     * 派发一次运行时事件。
     *
     * @param event 运行时事件。
     * @returns 本次事件产生的效果、变更和警告。
     */
    public dispatch(event: PropRuntimeEvent): PropMechanicsDispatchResult {
        const result: PropMechanicsDispatchResult = {
            event,
            effects: [],
            mutations: [],
            warnings: [],
        };
        this.activeDispatchResults.push(result);
        try {
            this.emit(event);
        } finally {
            this.activeDispatchResults.pop();
        }
        return result;
    }

    /**
     * 显式触发指定道具实例的回合开始入口。
     *
     * BattleManager 在回合开始阶段按“配方、辅料、盘面原料”的顺序逐个调用该方法；
     * 这里不会通过事件总线广播，因此 `roundStarted` 仍然保持为每轮一次的流程事件。
     *
     * @param item 本次需要触发回合开始逻辑的道具实例。
     * @param event 本次触发使用的执行上下文事件。
     * @returns 被命中的回合开始入口产生的派发结果。
     */
    public triggerPropTurnStart(item: RuntimePropInstance, event: PropRuntimeEvent): PropMechanicsDispatchResult[] {
        if (!item.alive || isEmptyPropInstance(item)) return [];
        return item.nodeRuntime?.triggerEntriesByType('eventTurnStart', event) ?? [];
    }

    /**
     * 在回合正式开始前预先计算本轮保护状态。
     *
     * 主动使用辅料发生在回合开始前，此时普通回合触发节点尚未执行，
     * 如果不提前准备保护状态，带有消除效果的辅料会绕过本轮应有的保护。
     * 该方法只处理从“回合开始”入口可达的保护动作，不推进计时器，
     * 不执行保护动作之后的流程分支，也不触发其它动作节点。
     */
    public prepareRoundProtection(): PropMechanicsDispatchResult {
        this.clearProtectedFlags();
        const event: PropRuntimeEvent = {
            type: 'roundStarted',
            reason: 'roundStarted',
        };
        const result: PropMechanicsDispatchResult = { event, effects: [], mutations: [], warnings: [] };

        for (const source of this.getRoundProtectionSources()) {
            const runtime = source.nodeRuntime;
            if (!runtime) continue;

            runtime.setExecution(event, result);
            try {
                for (const node of runtime.context.body.nodes) {
                    if (!node || node.type !== 'actionProtect') continue;
                    if (!this.isProtectNodeReachableFromTurnStart(runtime, node.id)) continue;
                    this.prepareProtectNode(runtime, node, source, result);
                }
            } finally {
                runtime.clearExecution();
            }
        }

        return result;
    }
    /**
     * 汇总事件节点执行结果。
     *
     * `dispatch()` 直接派发事件时会开启结果收集；BattleManager 直接通过事件总线派发时不收集，
     * 因为战斗流程只需要状态已经被修改，表现层可通过事件总线观察具体变化。
     *
     * @param result 某个入口事件节点执行后产生的结果。
     */
    public collectDispatchResult(result: PropMechanicsDispatchResult): void {
        const activeResult = this.activeDispatchResults[this.activeDispatchResults.length - 1];
        if (!activeResult || activeResult === result) return;
        activeResult.effects.push(...result.effects);
        activeResult.mutations.push(...result.mutations);
        activeResult.warnings.push(...result.warnings);
        if (result.releaseCallbacks?.length) {
            if (!activeResult.releaseCallbacks) activeResult.releaseCallbacks = [];
            activeResult.releaseCallbacks.push(...result.releaseCallbacks);
        }
    }

    /**
     * 读取当前最外层派发正在收集的结果对象。
     *
     * 事件节点被 `dispatch()` 触发时，所有后续动作应写入同一个结果对象；否则动作队列延迟执行后，
     * 调用方拿到的 dispatch 结果会缺少真实动作效果。
     *
     * @returns 当前活动派发结果；没有活动派发时返回 undefined。
     */
    public getActiveDispatchResult(): PropMechanicsDispatchResult | undefined {
        return this.activeDispatchResults[this.activeDispatchResults.length - 1];
    }

    /**
     * 通过统一入手管线创建真实道具实例。
     *
     * 该方法是生成、奖励、初始化材料库等真实道具来源的唯一入口。它会先创建底层实例并初始化节点运行时，
     * 然后依次派发创建、入手和持有列表变化事件，确保持有触发、表现层监听和运行时索引看到一致的生命周期。
     * “空位置”是盘面占位，不属于真实道具，必须走 `createEmptyBoardItem()` 而不是该方法。
     *
     * @param graph 道具原型运行时图。
     * @param source 可选来源实例，例如生成该道具的动作节点拥有者。
     * @returns 已完成入手流程的新实例。
     */
    public acquireProp(graph: PropRuntimeGraph, source?: RuntimePropInstance, options?: RuntimeAcquirePropOptions): RuntimePropInstance {
        const item = this.createRawInstance(graph);
        const reason = options?.reason ?? 'propAcquired';
        const event: PropRuntimeEvent = {
            type: 'propAcquired',
            item,
            source,
            prototype: graph,
            prototypeId: graph._id,
            reason,
        };
        this.emit(event);
        const boardPosition = this.resolveAcquiredBoardPosition(item, options);
        if (boardPosition) this.placeItem(item, boardPosition.row, boardPosition.col, reason);
        return item;
    }

    private resolveAcquiredBoardPosition(item: RuntimePropInstance, options?: RuntimeAcquirePropOptions): { row: number; col: number } | undefined {
        if (!this.canAcquiredItemEnterBoard(item)) return undefined;
        const preferredPositions = options?.positions?.filter((position) => position.row >= 0 && position.col >= 0) ?? [];
        while (preferredPositions.length) {
            const index = this.rng.int(0, preferredPositions.length);
            const [position] = preferredPositions.splice(index, 1);
            const emptySlot = this.getBoardItem(position.row, position.col);
            if (emptySlot && isEmptyPropInstance(emptySlot)) return { row: position.row, col: position.col };
        }
        const emptySlots = this.getAllBoardItems(true).filter(isEmptyPropInstance);
        if (!emptySlots.length) return undefined;
        const slot = emptySlots[this.rng.int(0, emptySlots.length)];
        return { row: slot.row, col: slot.col };
    }

    private canAcquiredItemEnterBoard(item: RuntimePropInstance): boolean {
        return this.isOwnedRealMaterial(item) && item.row < 0 && item.col < 0;
    }

    private createRawInstance(graph: PropRuntimeGraph, row = -1, col = -1): RuntimePropInstance {
        const item: RuntimePropInstance = {
            id: this.createInstanceId(graph),
            graph,
            row,
            col,
            alive: true,
            grow: 0,
            triggerCount: 0,
            useCount: 0,
            protectStacks: 0,
            collectedMagic: 0,
            meta: {},
            negativeEffects: [],
        };
        this.state.items.push(item);
        if (!isEmptyPropInstance(item)) this.addToOwnerList(item);
        if (row >= 0 && col >= 0) this.placeItem(item, row, col);
        item.nodeRuntime = this.createNodeRuntime(item);
        item.nodeRuntime.initialize();
        return item;
    }

    /**
     * 使用数据库中的“空位置”原型创建盘面占位实例。
     *
     * @param row 盘面行号。
     * @param col 盘面列号。
     * @returns 新创建的空位置实例。
     */
    public createEmptyBoardItem(row: number, col: number): RuntimePropInstance {
        const pooled = this.emptyBoardPool.pop();
        if (pooled) {
            this.resetEmptyBoardItem(pooled);
            pooled.alive = true;
            pooled.nodeRuntime = this.createNodeRuntime(pooled);
            pooled.nodeRuntime.initialize();
            this.placeItem(pooled, row, col);
            return pooled;
        }
        return this.createRawInstance(this.getEmptyPropGraph(), row, col);
    }

    /**
     * 确保盘面每个格子都有实例。
     *
     * 缺失或无效位置会补入数据库中的“空位置”实例。
     */
    public fillEmptyBoardSlots(): void {
        for (let rowIndex = 0; rowIndex < this.state.rows; rowIndex++) {
            if (!this.state.board[rowIndex]) this.state.board[rowIndex] = [];
            const row = this.state.firstRowIndex + rowIndex;
            for (let colIndex = 0; colIndex < this.state.cols; colIndex++) {
                const col = this.state.firstColumnIndex + colIndex;
                const existing = this.getBoardItem(row, col);
                if (!existing || !existing.alive) this.createEmptyBoardItem(row, col);
            }
        }
    }

    /**
     * 清空当前盘面。
     *
     * 真实原料只会离开盘面，不会从材料库删除；“空位置”实例会被回收到对象池，
     * 避免每轮准备和结束时频繁创建、销毁占位实例及其节点运行时。
     */
    public clearBoard(recycleEmpty = true): void {
        for (let rowIndex = 0; rowIndex < this.state.rows; rowIndex++) {
            if (!this.state.board[rowIndex]) this.state.board[rowIndex] = [];
            const row = this.state.firstRowIndex + rowIndex;
            for (let colIndex = 0; colIndex < this.state.cols; colIndex++) {
                const col = this.state.firstColumnIndex + colIndex;
                const item = this.getBoardItem(row, col);
                this.state.board[rowIndex][colIndex] = '';
                if (!item) continue;
                this.emit({ type: 'propLeftBoard', item, row, col, reason: 'clearBoard' });
                if (isEmptyPropInstance(item)) {
                    if (recycleEmpty) this.recycleEmptyBoardItem(item);
                    else {
                        this.disposeNodeRuntime(item);
                        this.resetEmptyBoardItem(item);
                    }
                    continue;
                }
                item.row = -1;
                item.col = -1;
            }
        }
    }
    /**
     * 读取指定坐标上的盘面实例。
     *
     * @param row 盘面行号。
     * @param col 盘面列号。
     * @returns 坐标合法且存在实例时返回实例，否则返回 undefined。
     */
    public getBoardItem(row: number, col: number): RuntimePropInstance | undefined {
        if (!this.isBoardPositionValid(row, col)) return undefined;
        const id = this.state.board[this.toBoardRowIndex(row)]?.[this.toBoardColumnIndex(col)];
        return id ? this.state.items.find((item) => item.id === id) : undefined;
    }

    /**
     * 将实例放入指定盘面坐标。
     *
     * 若目标位置已有“空位置”实例，会将该空位置标记为失效；若已有真实实例，则会被移出盘面。
     *
     * @param item 要放入盘面的实例。
     * @param row 目标行号。
     * @param col 目标列号。
     */
    public placeItem(item: RuntimePropInstance, row: number, col: number, reason: PropRuntimeEventReason = 'placeItem'): void {
        if (!this.isBoardPositionValid(row, col)) return;
        if (!isEmptyPropInstance(item) && !this.isOwnedRealMaterial(item)) {
            console.warn(`[${item.graph.name || item.graph._id}] cannot enter board before propAcquired or after removed`);
            return;
        }
        const rowIndex = this.toBoardRowIndex(row);
        const colIndex = this.toBoardColumnIndex(col);
        if (!this.state.board[rowIndex]) this.state.board[rowIndex] = [];
        const previous = this.getBoardItem(row, col);
        if (previous && previous.id !== item.id) {
            this.emit({ type: 'propLeftBoard', item: previous, row: previous.row, col: previous.col, reason: 'placeItemReplace' });
            previous.row = -1;
            previous.col = -1;
            if (isEmptyPropInstance(previous)) this.recycleEmptyBoardItem(previous);
        }
        if (this.isBoardPositionValid(item.row, item.col)
            && this.state.board[this.toBoardRowIndex(item.row)]?.[this.toBoardColumnIndex(item.col)] === item.id) {
            this.createEmptyBoardItem(item.row, item.col);
        }
        item.row = row;
        item.col = col;
        this.state.board[rowIndex][colIndex] = item.id;
        this.emit({ type: 'propEnteredBoard', item, row, col, reason });
        this.emitPositionEffectIfNeeded(item);
    }

    /**
     * 尝试把真实道具放入指定的“空位置”格子。
     *
     * 该方法只接受当前由数据库“空位置”原型占用的格子，不会挤出真实道具。
     * 生成动作使用它处理显式位置输入，保证“生成到盘面”仍然遵守空位规则。
     *
     * @param item 需要进入盘面的真实道具实例。
     * @param row 目标行号。
     * @param col 目标列号。
     * @returns 成功放入空位置时返回 true；位置非法或目标格不是空位置时返回 false。
     */
    /**
     * 直接交换两个盘面实例的位置。
     *
     * 交换位置不是离场再入场，也不是把实例替换成新实例，因此该方法不会调用 `placeItem()`，
     * 也不会触发 `propLeftBoard` 或 `propEnteredBoard`。表现层需要通过两次 `propPositionSwapped`
     * 事件读取每个实例的旧坐标、新坐标和交换对象，自行播放位移动画或刷新显示。
     *
     * @param left 第一个参与交换的盘面实例。
     * @param right 第二个参与交换的盘面实例。
     * @param source 触发交换动作的来源实例。
     * @param reason 交换事件原因，默认表示交换位置动作。
     * @returns 两个实例都在合法盘面坐标上且交换成功时返回 true。
     */
    public swapBoardItems(
        left: RuntimePropInstance,
        right: RuntimePropInstance,
        source?: RuntimePropInstance,
        reason: PropRuntimeEventReason = 'actionSwapPosition',
    ): boolean {
        if (left.id === right.id) return false;
        if (!left.alive || !right.alive) return false;
        if (!this.isBoardPositionValid(left.row, left.col) || !this.isBoardPositionValid(right.row, right.col)) return false;

        const leftRow = left.row;
        const leftCol = left.col;
        const rightRow = right.row;
        const rightCol = right.col;
        const leftRowIndex = this.toBoardRowIndex(leftRow);
        const leftColIndex = this.toBoardColumnIndex(leftCol);
        const rightRowIndex = this.toBoardRowIndex(rightRow);
        const rightColIndex = this.toBoardColumnIndex(rightCol);

        this.state.board[leftRowIndex][leftColIndex] = right.id;
        this.state.board[rightRowIndex][rightColIndex] = left.id;
        left.row = rightRow;
        left.col = rightCol;
        right.row = leftRow;
        right.col = leftCol;

        this.emit({
            type: 'propPositionSwapped',
            item: left,
            target: left,
            source,
            relatedItem: right,
            row: left.row,
            col: left.col,
            previousRow: leftRow,
            previousCol: leftCol,
            reason,
        });
        this.emit({
            type: 'propPositionSwapped',
            item: right,
            target: right,
            source,
            relatedItem: left,
            row: right.row,
            col: right.col,
            previousRow: rightRow,
            previousCol: rightCol,
            reason,
        });
        this.emitPositionEffectIfNeeded(left);
        this.emitPositionEffectIfNeeded(right);
        return true;
    }

    public placeItemToEmptySlot(item: RuntimePropInstance, row: number, col: number, reason: PropRuntimeEventReason = 'placeItem'): boolean {
        if (!isEmptyPropInstance(item) && !this.isOwnedRealMaterial(item)) {
            console.warn(`[${item.graph.name || item.graph._id}] cannot enter empty slot before propAcquired or after removed`);
            return false;
        }
        const emptySlot = this.getBoardItem(row, col);
        if (!emptySlot || !isEmptyPropInstance(emptySlot)) return false;
        this.placeItem(item, row, col, reason);
        return true;
    }

    /**
     * 尝试把真实道具放入一个随机“空位置”格子。
     *
     * 该方法用于生成动作没有显式位置输入的情况：生成实例先完成入手流程，
     * 再在当前盘面空位中随机选择一个落位。没有空位时只保留在持有列表中。
     *
     * @param item 需要进入盘面的真实道具实例。
     * @returns 成功放入随机空位置时返回 true；盘面没有空位置时返回 false。
     */
    public placeItemToRandomEmptySlot(item: RuntimePropInstance, reason: PropRuntimeEventReason = 'placeItem'): boolean {
        if (!isEmptyPropInstance(item) && !this.isOwnedRealMaterial(item)) {
            console.warn(`[${item.graph.name || item.graph._id}] cannot enter random empty slot before propAcquired or after removed`);
            return false;
        }
        const emptySlots = this.getAllBoardItems(true).filter(isEmptyPropInstance);
        if (!emptySlots.length) return false;
        const slot = emptySlots[this.rng.int(0, emptySlots.length)];
        return this.placeItemToEmptySlot(item, slot.row, slot.col, reason);
    }

    /**
     * 将实例从盘面移除。
     *
     * 移除后会立即用新的“空位置”实例补齐该格子。
     *
     * @param item 要移出盘面的实例。
     */
    public removeFromBoard(item: RuntimePropInstance): void {
        if (item.row < 0 || item.col < 0) return;
        const row = item.row;
        const col = item.col;
        const rowIndex = this.toBoardRowIndex(row);
        const colIndex = this.toBoardColumnIndex(col);
        if (this.state.board[rowIndex]?.[colIndex] === item.id) this.state.board[rowIndex][colIndex] = '';
        item.row = -1;
        item.col = -1;
        this.emit({ type: 'propLeftBoard', item, row, col, reason: 'removeFromBoard' });
        this.createEmptyBoardItem(row, col);
    }

    /**
     * 读取盘面实例列表。
     *
     * @param includeEmpty 是否包含“空位置”实例。
     * @returns 去重后的存活盘面实例。
     */
    public getAllBoardItems(includeEmpty = false): RuntimePropInstance[] {
        const result: RuntimePropInstance[] = [];
        for (let rowIndex = 0; rowIndex < this.state.rows; rowIndex++) {
            const row = this.state.firstRowIndex + rowIndex;
            for (let colIndex = 0; colIndex < this.state.cols; colIndex++) {
                const col = this.state.firstColumnIndex + colIndex;
                const item = this.getBoardItem(row, col);
                if (!item || !item.alive) continue;
                if (!includeEmpty && isEmptyPropInstance(item)) continue;
                result.push(item);
            }
        }
        return uniqueLiveItems(result, includeEmpty);
    }

    /**
     * 根据当前坐标触发“位置正确/位置错误”提示动画。
     *
     * 该逻辑只识别静态可判定的位置条件：动作节点条件为“绝对位置盘面目标集合包含自身”。
     * 判断过程不会执行运行时节点输出函数，避免随机筛选、概率条件等节点消耗随机数或改变上下文。
     */
    private emitPositionEffectIfNeeded(item: RuntimePropInstance): void {
        if (!item.alive || isEmptyPropInstance(item) || item.row < 0 || item.col < 0) return;
        const matched = this.evaluatePositionConditionState(item);
        if (matched === undefined) return;
        this.emit({
            type: 'animationTriggered',
            item,
            source: item,
            target: item,
            effectPaths: [matched ? 'positionEffectOn' : 'positionEffectOff'],
            animationScope: 'self',
            targetIds: [item.id],
            reason: 'positionEffectChanged',
        });
    }

    private evaluatePositionConditionState(item: RuntimePropInstance): boolean | undefined {
        const nodes = item.graph.graph.nodes;
        let hasPositionCondition = false;
        let hasMatchedPositionCondition = false;
        for (const node of nodes) {
            if (!isPropActionNode(node.type)) continue;
            for (const conditionNodeId of readRuntimeInputNodeIds(node, 'condition')) {
                const matched = this.evaluatePositionContainsSelfCondition(item, conditionNodeId, new Set<number>());
                if (matched === undefined) continue;
                hasPositionCondition = true;
                if (matched) hasMatchedPositionCondition = true;
            }
        }
        if (!hasPositionCondition) return undefined;
        return hasMatchedPositionCondition;
    }

    private evaluatePositionContainsSelfCondition(item: RuntimePropInstance, nodeId: number, visited: Set<number>): boolean | undefined {
        if (visited.has(nodeId)) return undefined;
        visited.add(nodeId);
        const node = findRuntimeNode(item.graph.graph, nodeId);
        if (!node || node.type !== 'logicContains') return undefined;
        const targetInputIds = readRuntimeInputNodeIds(node, 'target');
        if (targetInputIds.length && !targetInputIds.some((targetNodeId) => this.isSelfTargetChain(item, targetNodeId, new Set<number>()))) return undefined;
        const targetSourceIds = readRuntimeInputNodeIds(node, 'targets');
        let hasRecognizedTargetSource = false;
        let matched = false;
        for (const targetSourceId of targetSourceIds) {
            const result = this.evaluatePositionTargetChain(item, targetSourceId, new Set<number>());
            if (result === undefined) continue;
            hasRecognizedTargetSource = true;
            if (result) matched = true;
        }
        return hasRecognizedTargetSource ? matched : undefined;
    }

    private isSelfTargetChain(item: RuntimePropInstance, nodeId: number, visited: Set<number>): boolean {
        if (visited.has(nodeId)) return false;
        visited.add(nodeId);
        const node = findRuntimeNode(item.graph.graph, nodeId);
        if (!node) return false;
        if (node.type === 'targetSelf') return true;
        return readRuntimeInputNodeIds(node, 'target').some((inputNodeId) => this.isSelfTargetChain(item, inputNodeId, visited))
            || readRuntimeInputNodeIds(node, 'targets').some((inputNodeId) => this.isSelfTargetChain(item, inputNodeId, visited));
    }

    private evaluatePositionTargetChain(item: RuntimePropInstance, nodeId: number, visited: Set<number>): boolean | undefined {
        if (visited.has(nodeId)) return undefined;
        visited.add(nodeId);
        const body = item.graph.graph;
        const node = findRuntimeNode(body, nodeId);
        if (!node) return undefined;
        if (node.type === 'targetBoard') {
            if (String(node.data?.scope ?? 'all') !== 'absolute') return undefined;
            return isAbsolutePositionMatched(
                item,
                this.state.rows,
                this.state.cols,
                this.state.firstRowIndex,
                this.state.firstColumnIndex,
                normalizeRuntimePositions(node.data?.position, ['corner']),
            );
        }
        if (node.type === 'filterTag') {
            const upstream = this.evaluateSinglePositionTargetInput(item, node, visited);
            if (upstream !== true) return upstream;
            const tags = readStaticNumberInputs(body, node, 'tag');
            if (!tags.length) return true;
            const tagSet = new Set(tags);
            return item.graph.tags.some((tag) => tagSet.has(Number(tag)));
        }
        if (node.type === 'filterRarity') {
            const upstream = this.evaluateSinglePositionTargetInput(item, node, visited);
            if (upstream !== true) return upstream;
            const rarities = readStaticNumberInputs(body, node, 'rarity');
            if (!rarities.length) return true;
            return new Set(rarities).has(Number(item.graph.rarity));
        }
        if (node.type === 'filterPrototype') {
            const upstream = this.evaluateSinglePositionTargetInput(item, node, visited);
            if (upstream !== true) return upstream;
            const prototypeIds = readStaticPrototypeInputs(body, node, 'prototype');
            if (!prototypeIds.length) return true;
            return new Set(prototypeIds).has(item.graph._id);
        }
        return undefined;
    }

    private evaluateSinglePositionTargetInput(item: RuntimePropInstance, node: PropRuntimeNode, visited: Set<number>): boolean | undefined {
        const targetInputIds = readRuntimeInputNodeIds(node, 'targets');
        let hasRecognizedInput = false;
        let matched = false;
        for (const inputNodeId of targetInputIds) {
            const result = this.evaluatePositionTargetChain(item, inputNodeId, new Set(visited));
            if (result === undefined) continue;
            hasRecognizedInput = true;
            if (result) matched = true;
        }
        return hasRecognizedInput ? matched : undefined;
    }

    /** 将对外行坐标转换为内部 board 数组下标。 */
    private toBoardRowIndex(row: number): number {
        return row - this.state.firstRowIndex;
    }

    /** 将对外列坐标转换为内部 board 数组下标。 */
    private toBoardColumnIndex(col: number): number {
        return col - this.state.firstColumnIndex;
    }

    /** 判断对外坐标是否落在当前盘面范围内。 */
    private isBoardPositionValid(row: number, col: number): boolean {
        const rowIndex = this.toBoardRowIndex(row);
        const colIndex = this.toBoardColumnIndex(col);
        return rowIndex >= 0 && colIndex >= 0 && rowIndex < this.state.rows && colIndex < this.state.cols;
    }

    /**
     * 根据实例 id 读取运行时实例。
     *
     * @param id 实例 id。
     * @returns 找到时返回实例，否则返回 undefined。
     */
    public getItemById(id: string): RuntimePropInstance | undefined {
        return this.state.items.find((item) => item.id === id);
    }

    /**
     * 为当前回合增加魔力。
     *
     * @param delta 魔力变化量。
     * @param source 来源实例。
     * @param targets 受影响目标集合。
     * @param result 当前事件派发结果。
     */
    public addMagic(delta: number, source: RuntimePropInstance, targets: RuntimePropInstance[], result: PropMechanicsDispatchResult): void {
        const normalizedDelta = normalizeMagicDelta(delta);
        if (normalizedDelta === 0) return;
        this.state.currentRoundMagic += normalizedDelta;
        this.state.totalMagic += normalizedDelta;
        result.mutations.push({
            kind: 'magic',
            delta: normalizedDelta,
            currentRoundMagic: this.state.currentRoundMagic,
            totalMagic: this.state.totalMagic,
            sourceId: source.id,
        });
        this.emit({ type: 'magicChanged', source, item: source, magicDelta: normalizedDelta, targetIds: targets.map((item) => item.id), reason: 'magicChanged' });
    }

    /**
     * 登记永久加成规则。
     *
     * 规则只保存动作节点的目标输入和数值参数，不立即修改任何目标。每轮盘面确定后由
     * `prepareRoundYieldBoosts()` 重新筛选目标并写入本轮基础产出快照。
     */
    public registerPermanentBoostRule(source: RuntimePropInstance, actionNode: PropRuntimeNode): RuntimePermanentBoostRule | undefined {
        if (!this.hasInputConnection(actionNode, 'targets')) return undefined;
        const mutexTag = String(actionNode.data?.mutexTag ?? '').trim();
        const id = mutexTag ? `mutex:${mutexTag}` : `source:${source.id}:node:${actionNode.id}`;
        const body = this.createPermanentBoostRuleBody(source, actionNode);
        const rule: RuntimePermanentBoostRule = {
            id,
            sourceId: source.id,
            sourceGraphId: source.graph._id,
            actionNodeId: actionNode.id,
            body,
            keepAfterSourceRemoved: source.graph.type === 2,
        };
        const index = mutexTag
            ? this.state.permanentBoostRules.findIndex((item) => this.getPermanentBoostRuleMutexTag(item) === mutexTag)
            : this.state.permanentBoostRules.findIndex((item) => item.id === id);
        if (index >= 0) this.state.permanentBoostRules[index] = rule;
        else this.state.permanentBoostRules.push(rule);
        return rule;
    }

    /**
     * 准备当前回合的持续加成快照。
     *
     * 每条规则都会重新解析目标输入，因此它作用于当前盘面和当前材料库状态，而不是登记瞬间的目标快照。
     * 命中的目标不会立刻获得魔力；BattleManager 在该目标执行基础产出时按
     * `(yield + grow) * multiplier + add` 结算。
     */
    public prepareRoundYieldBoosts(): PropMechanicsDispatchResult {
        this.clearRoundYieldBoosts();
        const result: PropMechanicsDispatchResult = {
            event: {
                type: 'roundStarted',
                commissionIndex: this.state.commissionIndex,
                roundIndex: this.state.roundIndex,
                totalRoundIndex: this.state.totalRoundIndex,
                reason: 'prepareRoundYieldBoosts',
            },
            effects: [],
            mutations: [],
            warnings: [],
        };
        for (const rule of [...this.state.permanentBoostRules]) {
            const source = this.getItemById(rule.sourceId);
            if (!source) {
                this.removePermanentBoostRule(rule.id);
                continue;
            }
            if (!rule.keepAfterSourceRemoved && (!source.alive || source.graph._id !== rule.sourceGraphId)) {
                this.removePermanentBoostRule(rule.id);
                continue;
            }
            const runtime = this.createNodeRuntime(source, rule.body);
            runtime.setExecution(result.event, result);
            const actionNode = rule.body.nodes[rule.actionNodeId];
            if (!actionNode) {
                runtime.clearExecution();
                runtime.dispose();
                this.removePermanentBoostRule(rule.id);
                continue;
            }
            const targets = this.evaluatePermanentBoostTargets(runtime, actionNode, source, result);
            for (const target of targets) {
                runtime.context.withCurrentTarget(target, () => {
                    const matched = this.evaluatePermanentBoostCondition(runtime, actionNode, result);
                    if (!matched) return;
                    const amount = this.evaluatePermanentBoostNumber(runtime, actionNode, 'amount', 0, result);
                    const multiplier = this.evaluatePermanentBoostNumber(runtime, actionNode, 'multiplier', 1, result);
                    const boost = this.state.roundYieldBoosts[target.id] ?? {
                        multiplier: 1,
                        add: 0,
                        sourceIds: [],
                        ruleIds: [],
                    };
                    boost.multiplier *= multiplier;
                    boost.add += amount;
                    if (!boost.sourceIds.includes(source.id)) boost.sourceIds.push(source.id);
                    if (!boost.ruleIds.includes(rule.id)) boost.ruleIds.push(rule.id);
                    this.state.roundYieldBoosts[target.id] = boost;
                    result.effects.push({
                        kind: 'extraYield',
                        sourceId: source.id,
                        targetIds: [target.id],
                        amount,
                    });
                });
            }
            runtime.clearExecution();
            runtime.dispose();
        }
        return result;
    }

    /** 清理当前回合的持续加成快照。 */
    public clearRoundYieldBoosts(): void {
        this.state.roundYieldBoosts = {};
    }

    /**
     * 重建指定实例持有的节点运行时对象。
     *
     * 转化会替换实例的原型图，因此必须丢弃旧节点对象和旧节点内部状态，
     * 重新按新原型实例化节点。实例 id、位置、成长值等战斗状态保持不变。
     *
     * @param item 需要重建节点运行时的道具实例。
     * @returns 新建的节点运行时对象。
     */
    /**
     * 执行当前动作队列。
     *
     * 队列执行采用“每次取出前重新排序”的方式。这样动作执行过程中新增的后续 flow 能立即参与优先级排序，
     * 但它们一定是在产生它们的前置动作执行完成之后才会进入队列，因此不会破坏 flow 依赖。
     */
    public flushActionQueue(): void {
        if (this.actionBatchDepth > 0 || this.flushingActionQueue || !this.actionQueue.length) return;
        this.flushingActionQueue = true;
        let executedCount = 0;
        try {
            while (this.actionQueue.length) {
                this.actionQueue.sort(compareActionTask);
                const task = this.actionQueue.shift();
                if (!task) continue;
                executedCount += 1;
                if (executedCount > MAX_ACTION_TASKS_PER_FLUSH) {
                    const message = `道具动作队列超过 ${MAX_ACTION_TASKS_PER_FLUSH} 次执行，已中止本轮剩余动作。最近节点：${task.context.owner.graph.name}/${task.nodeType}`;
                    task.result.warnings.push(message);
                    console.warn(message);
                    this.actionQueue.length = 0;
                    break;
                }
                task.context.executeQueuedAction(task.nodeId, task.event, task.result, task.currentTarget, task.flowPath);
            }
        } finally {
            this.flushingActionQueue = false;
        }
    }

    private getActionSourceTypeOrder(owner: RuntimePropInstance): number {
        if (owner.graph.type === 3) return 0;
        if (owner.graph.type === 2) return 1;
        if (owner.graph.type === 1) return 2;
        return 3;
    }

    private getActionSourceOrder(owner: RuntimePropInstance): number {
        if (owner.graph.type === 3) return indexOrMax(this.state.recipes, owner.id);
        if (owner.graph.type === 2) return indexOrMax(this.state.consumables, owner.id);
        if (owner.graph.type === 1 && owner.row >= 0 && owner.col >= 0) {
            return this.toBoardRowIndex(owner.row) * this.state.cols + this.toBoardColumnIndex(owner.col);
        }
        if (owner.graph.type === 1) return indexOrMax(this.state.materialLibrary, owner.id);
        return indexOrMax(this.state.items.map((item) => item.id), owner.id);
    }

    public rebuildNodeRuntime(item: RuntimePropInstance): PropInstanceNodeRuntime {
        this.disposeNodeRuntime(item);
        item.nodeRuntime = this.createNodeRuntime(item);
        item.nodeRuntime.initialize();
        return item.nodeRuntime;
    }

    /**
     * 销毁指定实例持有的节点运行时。
     *
     * @param item 需要清理节点监听和节点长期状态的道具实例。
     */
    public disposeNodeRuntime(item: RuntimePropInstance): void {
        this.removePermanentBoostRulesBySource(item.id, false);
        item.nodeRuntime?.dispose();
        item.nodeRuntime = undefined;
    }

    /** 清除所有实例的本回合净化标记。 */
    public clearPurifiedFlags(): void {
        for (const item of this.state.items) delete item.purified;
    }

    /** 清除所有实例的本回合保护标记。 */
    public clearProtectedFlags(): void {
        for (const item of this.state.items) item.protectStacks = 0;
    }

    private getRoundProtectionSources(): RuntimePropInstance[] {
        const recipes = this.state.recipes
            .map((id) => this.getItemById(id))
            .filter((item): item is RuntimePropInstance => Boolean(item?.alive));
        const consumables = this.state.consumables
            .map((id) => this.getItemById(id))
            .filter((item): item is RuntimePropInstance => Boolean(item?.alive));
        return uniqueLiveItems([...recipes, ...consumables, ...this.getAllBoardItems(false)]);
    }

    private prepareProtectNode(
        runtime: PropInstanceNodeRuntime,
        actionNode: PropRuntimeNode,
        source: RuntimePropInstance,
        result: PropMechanicsDispatchResult,
    ): void {
        let targets: RuntimePropInstance[];
        try {
            targets = runtime.context.readInputTargets(actionNode, 'targets', []);
        } catch (error) {
            result.warnings.push(`${source.graph.name} prepare protection failed: ${String(error)}`);
            return;
        }

        for (const target of targets) {
            if (!this.isPrepareProtectionConditionMatched(runtime, actionNode, target, result)) continue;

            const previousStacks = target.protectStacks;
            target.protectStacks = Math.max(target.protectStacks, 1);
            if (target.protectStacks === previousStacks) continue;

            result.mutations.push({
                kind: 'protect',
                itemId: target.id,
                protectStacks: target.protectStacks,
                delta: target.protectStacks - previousStacks,
                sourceId: source.id,
            });
            result.effects.push({ kind: 'protect', sourceId: source.id, targetIds: [target.id], stacks: 1 });
        }
    }

    private isPrepareProtectionConditionMatched(
        runtime: PropInstanceNodeRuntime,
        actionNode: PropRuntimeNode,
        target: RuntimePropInstance,
        result: PropMechanicsDispatchResult,
    ): boolean {
        return runtime.context.withCurrentTarget(target, () => {
            const conditionNodeIds = runtime.context.readInputNodeIds(actionNode, 'condition');
            if (!conditionNodeIds.length) return true;

            for (const nodeId of conditionNodeIds) {
                const conditionNode = findRuntimeNode(runtime.context.body, nodeId);
                if (!conditionNode) {
                    result.warnings.push(`Missing protection condition node: ${nodeId}`);
                    return false;
                }
                const runtimeNode = runtime.context.createNode(conditionNode.id);
                if (!runtimeNode) return false;
                const value = runtimeNode.output(getDefaultOutputPort(runtimeNode.node.type));
                if (!toBoolean(value)) return false;
            }
            return true;
        });
    }

    private isProtectNodeReachableFromTurnStart(runtime: PropInstanceNodeRuntime, targetNodeId: number): boolean {
        const body = runtime.context.body;
        return body.entries
            .filter((entryId) => this.isTurnStartEntryReadyForProtection(runtime, entryId))
            .some((entryId) => this.isRuntimeNodeReachable(body, entryId, targetNodeId, new Set<number>()));
    }

    private isTurnStartEntryReadyForProtection(runtime: PropInstanceNodeRuntime, entryNodeId: number): boolean {
        const entryNode = findRuntimeNode(runtime.context.body, entryNodeId);
        if (entryNode?.type !== 'eventTurnStart') return false;
        const runtimeNode = runtime.context.createNode(entryNodeId) as unknown as {
            serializeState?: () => { timer?: number; triggeredCount?: number };
        } | undefined;
        const state = runtimeNode?.serializeState?.();
        const timer = Math.max(0, Math.floor(Number(state?.timer ?? 1)));
        const triggeredCount = Math.max(0, Math.floor(Number(state?.triggeredCount ?? 0)));
        const loop = Math.max(0, Math.floor(Number(entryNode.data?.loop ?? 0)));
        if (loop > 0 && triggeredCount >= loop) return false;
        return timer <= 1;
    }

    private isRuntimeNodeReachable(
        body: PropRuntimeGraphBody,
        currentNodeId: number,
        targetNodeId: number,
        visited: Set<number>,
    ): boolean {
        if (currentNodeId === targetNodeId) return true;
        if (visited.has(currentNodeId)) return false;
        visited.add(currentNodeId);

        const currentNode = findRuntimeNode(body, currentNodeId);
        if (!currentNode?.next) return false;

        for (const nextIds of Object.values(currentNode.next)) {
            for (const nextNodeId of nextIds) {
                if (this.isRuntimeNodeReachable(body, nextNodeId, targetNodeId, visited)) return true;
            }
        }
        return false;
    }
    private evaluatePermanentBoostTargets(runtime: PropInstanceNodeRuntime, actionNode: PropRuntimeNode, source: RuntimePropInstance, result: PropMechanicsDispatchResult): RuntimePropInstance[] {
        try {
            return runtime.context.readInputTargets(actionNode, 'targets', []);
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            result.warnings.push(`[${source.graph.name}] 永久加成规则目标筛选失败：${message}`);
            return [];
        }
    }

    private createPermanentBoostRuleBody(source: RuntimePropInstance, actionNode: PropRuntimeNode): PropRuntimeGraphBody {
        const nodes: PropRuntimeNode[] = [];
        const visited = new Set<number>();
        const visit = (nodeId: number): void => {
            if (visited.has(nodeId)) return;
            visited.add(nodeId);
            const node = source.graph.graph.nodes[nodeId];
            if (!node || this.isEventNode(node)) return;
            nodes[node.id] = this.cloneRuntimeNode(node);
            Object.keys(node.inputs ?? {}).forEach((portId) => {
                for (const inputNodeId of this.readRuntimeInputNodeIds(node, portId)) visit(inputNodeId);
            });
        };
        visit(actionNode.id);
        return { entries: [], nodes };
    }

    private evaluatePermanentBoostNumber(runtime: PropInstanceNodeRuntime, actionNode: PropRuntimeNode, portId: string, fallback: number, result: PropMechanicsDispatchResult): number {
        try {
            const nodeId = runtime.context.readInputNodeIds(actionNode, portId)[0];
            if (nodeId === undefined) return fallback;
            const runtimeNode = runtime.context.createNode(nodeId);
            if (!runtimeNode) return fallback;
            return toNumber(runtimeNode.output(getDefaultOutputPort(runtimeNode.node.type)), fallback);
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            result.warnings.push(`永久加成规则 ${portId} 计算失败：${message}`);
            return fallback;
        }
    }

    private evaluatePermanentBoostCondition(runtime: PropInstanceNodeRuntime, actionNode: PropRuntimeNode, result: PropMechanicsDispatchResult): boolean {
        try {
            const nodeIds = runtime.context.readInputNodeIds(actionNode, 'condition');
            if (!nodeIds.length) return true;
            return nodeIds.every((nodeId) => {
                const runtimeNode = runtime.context.createNode(nodeId);
                return runtimeNode ? toBoolean(runtimeNode.output(getDefaultOutputPort(runtimeNode.node.type)), false) : false;
            });
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            result.warnings.push(`永久加成规则条件计算失败：${message}`);
            return false;
        }
    }

    private getPermanentBoostRuleMutexTag(rule: RuntimePermanentBoostRule): string {
        const actionNode = rule.body.nodes[rule.actionNodeId];
        return String(actionNode?.data?.mutexTag ?? '').trim();
    }

    private readRuntimeInputNodeIds(node: PropRuntimeNode, portId: string): number[] {
        const raw = node.inputs?.[portId];
        if (raw === null || raw === undefined) return [];
        return Array.isArray(raw) ? raw : [raw];
    }

    private cloneRuntimeNode(node: PropRuntimeNode): PropRuntimeNode {
        return {
            id: node.id,
            sourceId: node.sourceId,
            type: node.type,
            data: node.data ? JSON.parse(JSON.stringify(node.data)) : undefined,
            inputs: node.inputs ? JSON.parse(JSON.stringify(node.inputs)) : undefined,
            next: undefined,
        };
    }

    private isEventNode(node: PropRuntimeNode): boolean {
        return node.type.indexOf('event') === 0;
    }

    private hasInputConnection(node: PropRuntimeNode, portId: string): boolean {
        const raw = node.inputs?.[portId];
        if (Array.isArray(raw)) return raw.length > 0;
        return raw !== null && raw !== undefined;
    }

    private removePermanentBoostRule(id: string): void {
        const index = this.state.permanentBoostRules.findIndex((rule) => rule.id === id);
        if (index >= 0) {
            this.state.permanentBoostRules.splice(index, 1);
            this.emit({ type: 'permanentBoostRulesChanged', reason: 'permanentBoostRuleRemoved' });
        }
    }

    private removePermanentBoostRulesBySource(sourceId: string, includePersistent: boolean): void {
        let changed = false;
        for (let index = this.state.permanentBoostRules.length - 1; index >= 0; index--) {
            const rule = this.state.permanentBoostRules[index];
            if (rule.sourceId !== sourceId) continue;
            if (!includePersistent && rule.keepAfterSourceRemoved) continue;
            this.state.permanentBoostRules.splice(index, 1);
            changed = true;
        }
        if (changed) this.emit({ type: 'permanentBoostRulesChanged', reason: 'permanentBoostRuleRemovedBySource' });
    }

    private recycleEmptyBoardItem(item: RuntimePropInstance): void {
        if (!isEmptyPropInstance(item)) return;
        this.disposeNodeRuntime(item);
        this.resetEmptyBoardItem(item);
        if (this.emptyBoardPool.indexOf(item) < 0) this.emptyBoardPool.push(item);
    }

    private resetEmptyBoardItem(item: RuntimePropInstance): void {
        item.row = -1;
        item.col = -1;
        item.alive = false;
        item.grow = 0;
        item.triggerCount = 0;
        item.useCount = 0;
        item.protectStacks = 0;
        item.collectedMagic = 0;
        item.meta = {};
        item.negativeEffects = [];
        delete item.purified;
    }

    private createNodeRuntime(item: RuntimePropInstance, bodyOverride?: PropRuntimeGraphBody): PropInstanceNodeRuntime {
        return new PropInstanceNodeRuntimeImpl(this, this.state, item, this.rng, this.createNodeRuntimeScope(item), bodyOverride);
    }

    private createNodeRuntimeScope(item: RuntimePropInstance): string {
        this.runtimeSeed += 1;
        return `prop-runtime:${item.id}:${this.runtimeSeed.toString(36)}`;
    }

    private createInstanceId(graph: PropRuntimeGraph): string {
        this.instanceSeed += 1;
        return `${graph._id}_${Date.now().toString(36)}_${this.instanceSeed.toString(36)}`;
    }

    private addToOwnerList(item: RuntimePropInstance): void {
        if (item.graph.type === 1 && this.state.materialLibrary.indexOf(item.id) < 0) this.state.materialLibrary.push(item.id);
        if (item.graph.type === 2 && this.state.consumables.indexOf(item.id) < 0) this.state.consumables.push(item.id);
        if (item.graph.type === 3 && this.state.recipes.indexOf(item.id) < 0) this.state.recipes.push(item.id);
    }

    private isOwnedRealMaterial(item: RuntimePropInstance): boolean {
        return Boolean(
            item.alive
            && item.graph.type === 1
            && !isEmptyPropInstance(item)
            && this.state.materialLibrary.indexOf(item.id) >= 0,
        );
    }

    private getEmptyPropGraph(): PropRuntimeGraph {
        const graph = this.state.catalog.find((item) => item.name === '空位置');
        if (!graph) throw new Error('缺少名称为“空位置”的道具原型，无法填充盘面空格');
        return graph;
    }
}

class PropInstanceNodeRuntimeImpl implements PropInstanceNodeRuntime {
    /** 节点共享的执行上下文；同一实例内所有节点都使用它访问战斗状态和当前派发结果。 */
    public readonly context: PropNodeContext;
    /** 按运行时节点 id 保存的节点实例缓存。 */
    public readonly nodes = new Map<number, RuntimeNodeLike>();
    /** 当前运行时图的入口事件节点缓存。 */
    public readonly entries: RuntimeNodeLike[] = [];

    /**
     * 创建单个道具实例的节点运行时。
     *
     * @param engine 道具机制引擎，用于访问战斗状态操作能力。
     * @param state 当前战斗运行时状态。
     * @param owner 持有这些节点的道具实例。
     * @param rng 随机源，供节点执行随机逻辑时使用。
     */
    public constructor(
        private readonly engine: PropsMechanicsEngine,
        private readonly state: BattleRuntimeState,
        public readonly owner: RuntimePropInstance,
        private readonly rng: PropRandomSource,
        public readonly scope: string,
        private readonly bodyOverride?: PropRuntimeGraphBody,
    ) {
        this.context = new PropNodeContext(this.engine, this.state, this.owner, this.rng, this.bodyOverride);
        this.context.bindRuntime(this);
        this.createNodes();
        this.entries = this.context.body.entries
            .map((nodeId) => this.getNode(nodeId))
            .filter((node): node is RuntimeNodeLike => Boolean(node));
    }

    /**
     * 读取已实例化的节点。
     *
     * @param nodeId 运行时节点 id。
     * @returns 命中时返回节点实例，否则返回 undefined。
     */
    public getNode(nodeId: number): RuntimeNodeLike | undefined {
        return this.nodes.get(nodeId);
    }

    /**
     * 初始化节点运行时。
     *
     * 事件节点会在该阶段向事件总线注册监听；数据节点和动作节点通常不需要初始化逻辑。
     */
    public initialize(): void {
        this.nodes.forEach((node) => node.initialize?.());
    }

    /**
     * 销毁节点运行时。
     *
     * 该方法会先允许节点释放自身状态，再按 scope 批量注销事件监听，防止被移除或转化后的旧节点继续响应事件。
     */
    public dispose(): void {
        this.nodes.forEach((node) => node.dispose?.());
        this.engine.getEventBus().clearScope(this.scope);
        this.nodes.clear();
        this.entries.length = 0;
    }

    /**
     * 执行一个入口事件节点的后续 flow。
     *
     * @param entryNode 已命中的入口事件节点实例。
     * @param event 触发该入口的运行时事件。
     * @returns 本次入口执行产生的效果、状态变更和警告记录。
     */
    public executeEntry(entryNode: RuntimeNodeLike, event: PropRuntimeEvent): PropMechanicsDispatchResult {
        const result: PropMechanicsDispatchResult = this.engine.getActiveDispatchResult() ?? {
            event,
            effects: [],
            mutations: [],
            warnings: [],
        };
        this.setExecution(event, result);
        try {
            this.context.runNext(entryNode.node.next?.flow);
        } finally {
            this.clearExecution();
        }
        this.engine.collectDispatchResult(result);
        return result;
    }

    /**
     * 按事件节点类型显式触发当前实例的入口节点。
     *
     * 该方法用于非广播式流程，例如 BattleManager 逐个道具调用回合开始逻辑。
     * 只有实现了 `trigger()` 的事件节点会被调用；未命中或计时器未到期时不会产生结果。
     *
     * @param type 入口事件节点类型。
     * @param event 本次触发的执行上下文事件。
     * @returns 实际执行 flow 的入口节点结果。
     */
    public triggerEntriesByType(type: string, event: PropRuntimeEvent): PropMechanicsDispatchResult[] {
        const results: PropMechanicsDispatchResult[] = [];
        for (const entryNode of this.entries) {
            if (entryNode.node.type !== type || !entryNode.trigger) continue;
            const result = entryNode.trigger(event);
            if (result) results.push(result);
        }
        return results;
    }

    /**
     * 绑定一次事件派发的临时结果对象。
     *
     * @param event 当前派发事件。
     * @param result 当前派发结果，节点产生的效果、变更和警告都会写入这里。
     */
    public setExecution(event: PropRuntimeEvent, result: PropMechanicsDispatchResult): void {
        this.context.begin(event, result);
    }

    /**
     * 清理一次事件派发后的临时执行状态。
     *
     * 节点自身的长期状态不会被清理，例如回合计时器和循环次数。
     */
    public clearExecution(): void {
        this.context.end();
    }

    private createNodes(): void {
        const nodes = this.context.body.nodes;
        for (let index = 0; index < nodes.length; index++) {
            const node = nodes[index];
            if (!node) continue;
            const runtimeNode = createRuntimeNode(node, this.context);
            if (runtimeNode) this.nodes.set(node.id, runtimeNode);
            else this.context.warn(`节点类型 ${node.type} 未注册运行时 class`);
        }
    }
}

class PropNodeContext implements RuntimeNodeContext {
    /** 当前运行时图主体。 */
    public readonly body: PropRuntimeGraphBody;
    /** 当前节点链共享变量。 */
    public readonly variables: Record<string, unknown> = {};
    /** each 分支执行时的当前目标。 */
    public currentTarget?: RuntimePropInstance;
    private activeFlowPath: number[] = [];

    private readonly executing = new Set<number>();
    private readonly pendingWarnings: string[] = [];
    private readonly pendingEffects: PropRuntimeEffect[] = [];
    private readonly pendingMutations: PropRuntimeMutation[] = [];
    private runtime?: PropInstanceNodeRuntime;
    private activeEvent: PropRuntimeEvent = { type: 'gameStarted', reason: 'initial' };
    private activeResult?: PropMechanicsDispatchResult;

    /**
     * 创建道具实例长期节点上下文。
     *
     * 上下文随节点运行时一起创建并长期保留，事件派发时只临时绑定 `event/result`。
     * 因此节点内部的计时器、循环次数等状态可以跨回合保存。
     *
     * @param engine 道具机制引擎。
     * @param state 战斗运行时状态。
     * @param owner 当前运行时图拥有者。
     * @param rng 随机源。
     */
    public constructor(
        private readonly engine: PropsMechanicsEngine,
        public readonly state: BattleRuntimeState,
        public readonly owner: RuntimePropInstance,
        public readonly rng: PropRandomSource,
        bodyOverride?: PropRuntimeGraphBody,
    ) {
        this.graph = owner.graph;
        this.body = bodyOverride ?? owner.graph.graph;
    }

    /** 当前节点运行时创建时绑定的道具原型图；转化后的新原型会创建新的上下文。 */
    public readonly graph: PropRuntimeGraph;

    /** 当前正在派发的运行时事件。 */
    public get event(): PropRuntimeEvent {
        return this.activeEvent;
    }

    /** 当前事件期间产生的警告；未处于事件派发时暂存到 pendingWarnings。 */
    public get warnings(): string[] {
        return this.activeResult?.warnings ?? this.pendingWarnings;
    }

    /** 当前事件期间产生的效果记录；未处于事件派发时暂存到 pendingEffects。 */
    public get effects(): PropRuntimeEffect[] {
        return this.activeResult?.effects ?? this.pendingEffects;
    }

    /** 当前事件期间产生的状态变更记录；未处于事件派发时暂存到 pendingMutations。 */
    public get mutations(): PropRuntimeMutation[] {
        return this.activeResult?.mutations ?? this.pendingMutations;
    }

    /**
     * 绑定持有该上下文的节点运行时对象。
     *
     * @param runtime 当前道具实例的节点运行时。
     */
    public bindRuntime(runtime: PropInstanceNodeRuntime): void {
        this.runtime = runtime;
    }

    /**
     * 开始一次事件派发。
     *
     * @param event 当前派发事件。
     * @param result 当前派发结果，节点执行期间产生的记录会写入该对象。
     */
    public begin(event: PropRuntimeEvent, result: PropMechanicsDispatchResult): void {
        this.activeEvent = event;
        this.activeResult = result;
        if (this.pendingWarnings.length) result.warnings.push(...this.pendingWarnings.splice(0));
        if (this.pendingEffects.length) result.effects.push(...this.pendingEffects.splice(0));
        if (this.pendingMutations.length) result.mutations.push(...this.pendingMutations.splice(0));
    }

    /**
     * 结束一次事件派发。
     *
     * 仅清理当前目标、递归保护和活动结果；不清理节点自身长期状态。
     */
    public end(): void {
        this.currentTarget = undefined;
        this.executing.clear();
        this.activeResult = undefined;
    }

    public withCurrentTarget<T>(target: RuntimePropInstance | undefined, callback: () => T): T {
        const previousTarget = this.currentTarget;
        this.currentTarget = target;
        try {
            return callback();
        } finally {
            this.currentTarget = previousTarget;
        }
    }

    /**
     * 根据节点 id 创建节点 class 实例。
     *
     * @param nodeId 运行时节点 id。
     * @returns 节点实例；节点不存在或类型未注册时返回 undefined。
     */
    public createNode(nodeId: number): RuntimeNodeLike | undefined {
        const runtimeNode = this.runtime?.getNode(nodeId);
        if (!runtimeNode) this.warn(`节点 ${nodeId} 不存在或未实例化`);
        return runtimeNode;
    }

    /**
     * 读取运行时节点定义。
     *
     * @param nodeId 运行时节点 id。
     * @returns 节点定义；不存在时返回 undefined。
     */
    public getNode(nodeId: number): PropRuntimeNode | undefined {
        return this.body.nodes[nodeId];
    }

    /**
     * 读取输入端口连接的节点 id。
     *
     * @param node 当前节点定义。
     * @param portId 输入端口名称。
     * @returns 连接节点 id 列表。
     */
    public readInputNodeIds(node: PropRuntimeNode, portId: string): number[] {
        const raw = node.inputs?.[portId];
        if (raw === null || raw === undefined) return [];
        return Array.isArray(raw) ? raw : [raw];
    }

    /**
     * 执行流程后继节点。
     *
     * @param nodeIds 后继动作节点 id 列表。
     * @param currentTarget each 分支当前目标。
     */
    public runNext(nodeIds: number[] | undefined, currentTarget?: RuntimePropInstance): void {
        if (!nodeIds?.length) return;
        if (!this.activeResult) {
            this.warn('当前没有活动派发结果，无法调度后续动作');
            return;
        }
        const target = currentTarget ?? this.currentTarget;
        nodeIds.forEach((nodeId, index) => {
            this.engine.enqueueAction(this, nodeId, target, this.activeEvent, this.activeResult as PropMechanicsDispatchResult, index, [...this.activeFlowPath, index]);
        });
    }

    /**
     * 读取盘面实例。
     *
     * @param includeEmpty 是否包含“空位置”实例。
     * @returns 盘面实例列表。
     */
    public getAllBoardItems(includeEmpty = false): RuntimePropInstance[] {
        return this.engine.getAllBoardItems(includeEmpty);
    }

    /**
     * 根据实例 id 读取运行时实例。
     *
     * @param id 实例 id。
     * @returns 找到时返回实例，否则返回 undefined。
     */
    public getItemById(id: string): RuntimePropInstance | undefined {
        return this.engine.getItemById(id);
    }

    /**
     * 读取指定盘面坐标上的实例。
     *
     * @param row 盘面行号。
     * @param col 盘面列号。
     * @returns 找到时返回实例，否则返回 undefined。
     */
    public getBoardItem(row: number, col: number): RuntimePropInstance | undefined {
        return this.engine.getBoardItem(row, col);
    }

    /**
     * 将实例放入指定盘面坐标。
     *
     * @param item 要放置的实例。
     * @param row 目标行号。
     * @param col 目标列号。
     */
    public placeItem(item: RuntimePropInstance, row: number, col: number): void {
        this.engine.placeItem(item, row, col);
    }

    /**
     * 直接交换两个盘面实例的位置。
     *
     * 该方法专供交换位置动作使用，底层不会触发离场或入场事件，只会通过 `propPositionSwapped`
     * 通知表现层两个实例的坐标发生了变化。
     *
     * @param left 第一个参与交换的盘面实例。
     * @param right 第二个参与交换的盘面实例。
     * @param source 触发交换的来源实例。
     * @param reason 交换事件原因。
     * @returns 交换成功时返回 true。
     */
    public swapBoardItems(
        left: RuntimePropInstance,
        right: RuntimePropInstance,
        source?: RuntimePropInstance,
        reason?: PropRuntimeEventReason,
    ): boolean {
        return this.engine.swapBoardItems(left, right, source, reason);
    }

    /**
     * 尝试把实例放入指定空位；目标格不是“空位置”时不会改变盘面。
     *
     * @param item 需要进入盘面的真实道具实例。
     * @param row 目标行号。
     * @param col 目标列号。
     * @returns 成功落位时返回 true。
     */
    public placeItemToEmptySlot(item: RuntimePropInstance, row: number, col: number, reason?: PropRuntimeEventReason): boolean {
        return this.engine.placeItemToEmptySlot(item, row, col, reason);
    }

    /**
     * 尝试把实例放入随机空位；没有空位时保持实例只在持有列表中。
     *
     * @param item 需要进入盘面的真实道具实例。
     * @returns 成功落位时返回 true。
     */
    public placeItemToRandomEmptySlot(item: RuntimePropInstance, reason?: PropRuntimeEventReason): boolean {
        return this.engine.placeItemToRandomEmptySlot(item, reason);
    }

    /**
     * 将实例从盘面移除。
     *
     * @param item 要移除的实例。
     */
    public removeFromBoard(item: RuntimePropInstance): void {
        this.engine.removeFromBoard(item);
    }

    /**
     * 通过统一入手管线创建真实道具实例。
     *
     * 动作节点需要生成道具时只能调用该方法，不能绕过入手管线直接构造实例。这样可以保证
     * `propAcquired` 和持有列表变化事件全部带上当前节点拥有者作为来源。
     *
     * @param graph 道具原型运行时图。
     * @returns 已完成入手流程的新实例。
     */
    public acquireProp(graph: PropRuntimeGraph, options?: RuntimeAcquirePropOptions): RuntimePropInstance {
        return this.engine.acquireProp(graph, this.owner, options);
    }

    /**
     * 重建指定实例持有的节点运行时。
     *
     * @param item 被转化或需要刷新逻辑的道具实例。
     */
    public rebuildNodeRuntime(item: RuntimePropInstance): void {
        this.engine.rebuildNodeRuntime(item);
    }

    /**
     * 注册跟随当前节点运行时生命周期的事件监听。
     *
     * @param type 要监听的事件类型。
     * @param listener 事件回调；回调内应只执行短逻辑，避免阻塞同步事件总线。
     * @returns 注销当前监听的函数。
     */
    public listen(type: PropRuntimeEvent['type'], listener: (event: PropRuntimeEvent) => void): () => void {
        const scope = this.runtime?.scope ?? `prop-runtime:${this.owner.id}:unbound`;
        return this.engine.getEventBus().on(type, listener, scope);
    }

    /**
     * 请求当前实例节点运行时执行入口 flow。
     *
     * @param entryNode 已命中的入口事件节点。
     * @param event 触发入口的运行时事件。
     * @returns 本次入口执行结果；运行时未绑定时返回带警告的空结果。
     */
    public executeEntry(entryNode: RuntimeNodeLike, event: PropRuntimeEvent): PropMechanicsDispatchResult {
        if (!this.runtime) {
            const result: PropMechanicsDispatchResult = { event, effects: [], mutations: [], warnings: [] };
            result.warnings.push(`[${this.graph.name}] 节点运行时尚未绑定，无法执行入口 ${entryNode.node.sourceId}`);
            return result;
        }
        return this.runtime.executeEntry(entryNode, event);
    }

    /**
     * 销毁指定实例的节点运行时。
     *
     * @param item 被移除、消除或转化前需要清理旧监听的实例。
     */
    public disposeNodeRuntime(item: RuntimePropInstance): void {
        this.engine.disposeNodeRuntime(item);
    }

    /**
     * 派发运行时事件。
     *
     * @param event 要派发的事件。
     */
    public emit(event: PropRuntimeEvent): void {
        this.engine.emit(event);
    }

    /**
     * 增加当前回合魔力。
     *
     * @param delta 魔力变化量。
     * @param source 来源实例。
     * @param targets 受影响目标。
     */
    public addMagic(delta: number, source: RuntimePropInstance, targets: RuntimePropInstance[]): void {
        if (!this.activeResult) {
            this.warn('当前没有活动派发结果，无法写入魔力变更');
            return;
        }
        this.engine.addMagic(delta, source, targets, this.activeResult);
    }

    /**
     * 记录非致命警告。
     *
     * @param message 警告内容。
     */
    /**
     * 登记永久加成规则。
     *
     * @param node 触发登记的动作节点。
     * @param amount 固定魔力增量。
     * @param multiplier 按目标基础产出和成长计算的倍率。
     * @param mutexTag 互斥标签；相同标签只保留最新规则。
     */
    public registerPermanentBoostRule(node: PropRuntimeNode): RuntimePermanentBoostRule | undefined {
        return this.engine.registerPermanentBoostRule(this.owner, node);
    }

    /**
     * 登记持有触发效果释放时需要执行的回调。
     *
     * 持有触发在来源道具移除时需要撤销已经登记到全局状态中的持续效果。
     * 回调记录在当前派发结果中，由事件节点保存并在释放时统一调用。
     */
    public registerReleaseCallback(callback: PropRuntimeReleaseCallback): void {
        if (!this.activeResult) {
            this.warn('当前没有活动派发结果，无法登记持有释放回调');
            return;
        }
        if (!this.activeResult.releaseCallbacks) this.activeResult.releaseCallbacks = [];
        this.activeResult.releaseCallbacks.push(callback);
    }

    /**
     * 记录非致命警告。
     *
     * @param message 警告内容。
     */
    public warn(message: string): void {
        this.warnings.push(`[${this.graph.name}] ${message}`);
    }

    /**
     * 在调度器恢复的事件上下文中执行一个动作节点。
     *
     * 动作任务可能在事件监听器返回之后才真正执行，因此必须显式恢复入队时捕获的事件、结果对象和 each 当前目标。
     * 这样动作产生的效果、状态变更和警告仍然会写入正确的派发结果，并且输入节点仍能读取到正确的当前目标。
     *
     * @param nodeId 需要执行的动作节点 id。
     * @param event 入队时所属的原始事件。
     * @param result 入队时应写入的派发结果。
     * @param currentTarget each 分支传递下来的当前目标。
     */
    public executeQueuedAction(
        nodeId: number,
        event: PropRuntimeEvent,
        result: PropMechanicsDispatchResult,
        currentTarget?: RuntimePropInstance,
        flowPath: number[] = [],
    ): void {
        const previousEvent = this.activeEvent;
        const previousResult = this.activeResult;
        const previousTarget = this.currentTarget;
        const previousFlowPath = this.activeFlowPath;
        this.activeEvent = event;
        this.activeResult = result;
        this.currentTarget = currentTarget;
        this.activeFlowPath = flowPath;
        try {
            this.runAction(nodeId);
        } finally {
            this.currentTarget = previousTarget;
            this.activeFlowPath = previousFlowPath;
            this.activeResult = previousResult;
            this.activeEvent = previousEvent;
        }
    }

    /**
     * 读取一个输入端口输出的目标集合。
     *
     * @param node 当前节点。
     * @param portId 输入端口名称。
     * @param fallback 未连接时使用的默认目标。
     * @returns 目标实例列表。
     */
    public readInputTargets(node: PropRuntimeNode, portId: string, fallback: RuntimePropInstance[] = []): RuntimePropInstance[] {
        const result: RuntimePropInstance[] = [];
        for (const nodeId of this.readInputNodeIds(node, portId)) {
            const value = this.readNodeDefaultOutput(nodeId);
            if (!Array.isArray(value)) continue;
            for (const item of value) {
                if (item && typeof item === 'object' && 'id' in item && 'graph' in item) result.push(item as RuntimePropInstance);
            }
        }
        return result.length ? uniqueLiveItems(result) : fallback;
    }

    private readNodeDefaultOutput(nodeId: number): PropNodeValue {
        const runtimeNode = this.createNode(nodeId);
        if (!runtimeNode) return undefined;
        return runtimeNode.output(getDefaultOutputPort(runtimeNode.node.type));
    }

    private runAction(nodeId: number): void {
        const runtimeNode = this.createNode(nodeId);
        if (!runtimeNode) return;
        if (!isPropActionNode(runtimeNode.node.type) || !isRuntimeActionNode(runtimeNode)) {
            this.warn(`流程目标 ${runtimeNode.node.sourceId} 不是动作节点`);
            return;
        }
        if (this.executing.has(nodeId)) {
            this.warn(`动作节点 ${runtimeNode.node.sourceId} 出现递归流程`);
            return;
        }
        this.executing.add(nodeId);
        const skippedNegativeResult = this.trySkipPurifiedNegativeAction(runtimeNode.node);
        const actionResult = skippedNegativeResult ?? runtimeNode.execute();
        this.executing.delete(nodeId);
        if (!skippedNegativeResult) this.emitActionAnimations(runtimeNode.node, actionResult);
        this.registerUnsupportedAcquireRelease(runtimeNode.node, actionResult);
        this.advanceActionFlow(runtimeNode.node, actionResult);
    }

    private trySkipPurifiedNegativeAction(node: PropRuntimeNode): PropActionResult | undefined {
        if (!this.owner.purified || node.data?.negative !== true) return undefined;
        const targets = this.readActionTargetsForSkippedNegative(node);
        return targets.length
            ? { success: true, eachTargets: targets }
            : { success: false, eachTargets: [] };
    }

    private readActionTargetsForSkippedNegative(node: PropRuntimeNode): RuntimePropInstance[] {
        const result: RuntimePropInstance[] = [];
        for (const nodeId of this.readInputNodeIds(node, 'targets')) {
            const value = this.readNodeDefaultOutput(nodeId);
            if (!Array.isArray(value)) continue;
            for (const item of value) {
                if (item && typeof item === 'object' && 'id' in item && 'graph' in item) result.push(item as RuntimePropInstance);
            }
        }
        return uniqueLiveItems(result, true);
    }

    private emitActionAnimations(node: PropRuntimeNode, actionResult: PropActionResult): void {
        if (!actionResult.success) return;
        const selfAnimations = readResourcePaths(node.data?.selfAnimations);
        const targetAnimations = readResourcePaths(node.data?.targetAnimations);
        const selfEffects = readResourcePaths(node.data?.selfEffects);
        const targetEffects = readResourcePaths(node.data?.targetEffects);
        if (selfAnimations.length || selfEffects.length) {
            this.emit({
                type: 'animationTriggered',
                item: this.owner,
                source: this.owner,
                target: this.owner,
                animationPaths: selfAnimations,
                effectPaths: selfEffects,
                animationScope: 'self',
                actionNodeId: node.id,
                actionType: node.type,
                targetIds: [this.owner.id],
                reason: 'animationTriggered',
            });
        }
        if (!targetAnimations.length && !targetEffects.length) return;
        const seen = new Set<string>();
        for (const target of actionResult.eachTargets ?? []) {
            if (!target || seen.has(target.id)) continue;
            seen.add(target.id);
            this.emit({
                type: 'animationTriggered',
                item: target,
                source: this.owner,
                target,
                animationPaths: targetAnimations,
                effectPaths: targetEffects,
                animationScope: 'target',
                actionNodeId: node.id,
                actionType: node.type,
                targetIds: [target.id],
                reason: 'animationTriggered',
            });
        }
    }

    private registerUnsupportedAcquireRelease(node: PropRuntimeNode, actionResult: PropActionResult): void {
        if (!actionResult.success) return;
        if (this.activeEvent.type !== 'propAcquired') return;
        if (node.type === 'actionRarityWeight') return;
        const ownerName = this.owner.graph.name || this.owner.graph._id;
        this.registerReleaseCallback(() => {
            console.warn(`[${ownerName}] 持有触发动作 ${node.type} 暂不支持自动释放，已保留原效果`);
        });
    }

    private advanceActionFlow(node: PropRuntimeNode, actionResult: PropActionResult): void {
        if (actionResult.success) {
            for (const target of actionResult.eachTargets ?? []) this.runNext(node.next?.each, target);
            this.runNext(node.next?.complete);
        } else {
            this.runNext(node.next?.fail);
        }
    }
}

function compareActionTask(a: RuntimeActionTask, b: RuntimeActionTask): number {
    if (a.context === b.context && a.event === b.event) {
        const flowPathOrder = compareFlowPath(a.flowPath, b.flowPath);
        if (flowPathOrder !== 0) return flowPathOrder;
    }
    if (a.priority !== b.priority) return a.priority - b.priority;
    if (a.sourceTypeOrder !== b.sourceTypeOrder) return a.sourceTypeOrder - b.sourceTypeOrder;
    if (a.sourceOrder !== b.sourceOrder) return a.sourceOrder - b.sourceOrder;
    if (a.flowOrder !== b.flowOrder) return a.flowOrder - b.flowOrder;
    return a.sequence - b.sequence;
}

function compareFlowPath(a: number[], b: number[]): number {
    const length = Math.min(a.length, b.length);
    for (let index = 0; index < length; index++) {
        const diff = a[index] - b[index];
        if (diff !== 0) return diff;
    }
    return a.length - b.length;
}

function indexOrMax(list: string[], id: string): number {
    const index = list.indexOf(id);
    return index >= 0 ? index : Number.MAX_SAFE_INTEGER;
}

function readResourcePaths(value: unknown): string[] {
    const values = Array.isArray(value) ? value : value ? [value] : [];
    return values
        .map((item) => String(item).trim())
        .filter(Boolean);
}

function readRuntimeInputNodeIds(node: PropRuntimeNode, portId: string): number[] {
    const raw = node.inputs?.[portId];
    if (raw === null || raw === undefined) return [];
    return Array.isArray(raw) ? raw : [raw];
}

function findRuntimeNode(body: PropRuntimeGraphBody, nodeId: number): PropRuntimeNode | undefined {
    return body.nodes.find((node) => node.id === nodeId);
}

function normalizeRuntimePositions(rawPosition: unknown, fallback: string[]): string[] {
    const raw = Array.isArray(rawPosition) ? rawPosition : rawPosition ? [rawPosition] : fallback;
    const result = raw.map((item) => String(item)).filter(Boolean);
    return result.length ? result : fallback;
}

function isAbsolutePositionMatched(
    item: RuntimePropInstance,
    rows: number,
    cols: number,
    firstRow: number,
    firstCol: number,
    positions: string[],
): boolean {
    const lastRow = firstRow + rows - 1;
    const lastCol = firstCol + cols - 1;
    const middleCol = firstCol + Math.floor(cols / 2);
    return positions.some((position) => {
        if (position === 'corner') return (item.row === firstRow || item.row === lastRow) && (item.col === firstCol || item.col === lastCol);
        if (position === 'edge') return item.row === firstRow || item.row === lastRow || item.col === firstCol || item.col === lastCol;
        if (position === 'firstRow') return item.row === firstRow;
        if (position === 'lastRow') return item.row === lastRow;
        if (position === 'firstColumn') return item.col === firstCol;
        if (position === 'lastColumn') return item.col === lastCol;
        if (position === 'middleColumn') return item.col === middleCol;
        return false;
    });
}

function readStaticNumberInputs(body: PropRuntimeGraphBody, node: PropRuntimeNode, portId: string): number[] {
    return readRuntimeInputNodeIds(node, portId)
        .map((nodeId) => findRuntimeNode(body, nodeId))
        .map((inputNode) => Number(inputNode?.data?.value))
        .filter((value) => Number.isFinite(value));
}

function readStaticPrototypeInputs(body: PropRuntimeGraphBody, node: PropRuntimeNode, portId: string): string[] {
    return readRuntimeInputNodeIds(node, portId)
        .map((nodeId) => findRuntimeNode(body, nodeId))
        .map((inputNode) => String(inputNode?.data?.prototypeId ?? '').trim())
        .filter(Boolean);
}
